import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmWord?: string;
  requireReason?: boolean;
  destructive?: boolean;
  onConfirm: (reason?: string) => Promise<void> | void;
  confirmLabel?: string;
}

/**
 * Re-confirmation modal for sensitive admin actions.
 * Forces typing a confirm word and (optionally) a reason — both are recorded.
 */
export const ConfirmActionDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmWord = "CONFIRM",
  requireReason = true,
  destructive = true,
  onConfirm,
  confirmLabel = "Execute",
}: Props) => {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const ok = typed === confirmWord && (!requireReason || reason.trim().length >= 3);

  const handle = async () => {
    if (!ok) return;
    setBusy(true);
    try {
      await onConfirm(requireReason ? reason.trim() : undefined);
      setTyped("");
      setReason("");
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {destructive && <AlertTriangle className="h-5 w-5 text-destructive" />}
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {requireReason && (
            <div className="space-y-1.5">
              <Label htmlFor="reason">Reason (recorded in audit log)</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you doing this?"
                rows={2}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="confirm">
              Type <span className="font-mono font-bold">{confirmWord}</span> to proceed
            </Label>
            <Input
              id="confirm"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmWord}
              autoComplete="off"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            disabled={!ok || busy}
            onClick={handle}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
